import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { SitemapEntry, SitemapGenerationLog, SitemapStats } from '@/types/sitemap';

export const useSitemapManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'static' | 'dynamic' | 'generated'>('all');
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch sitemap entries
  const { data: entries = [], isLoading: entriesLoading, error: entriesError } = useQuery({
    queryKey: ['sitemap-entries', searchTerm, filterType],
    queryFn: async () => {
      let query = supabase
        .from('sitemap_entries')
        .select('*')
        .order('updated_at', { ascending: false });

      if (searchTerm) {
        query = query.ilike('url', `%${searchTerm}%`);
      }

      if (filterType !== 'all') {
        query = query.eq('entry_type', filterType);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as SitemapEntry[];
    },
  });

  // Fetch generation logs
  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['sitemap-generation-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sitemap_generation_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as SitemapGenerationLog[];
    },
  });

  // Fetch statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['sitemap-stats'],
    queryFn: async () => {
      const { data: entriesData, error: entriesError } = await supabase
        .from('sitemap_entries')
        .select('entry_type, is_active');

      if (entriesError) throw entriesError;

      const { data: logsData, error: logsError } = await supabase
        .from('sitemap_generation_logs')
        .select('created_at')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1);

      if (logsError) throw logsError;

      const totalEntries = entriesData?.length || 0;
      const activeEntries = entriesData?.filter(e => e.is_active).length || 0;
      const staticEntries = entriesData?.filter(e => e.entry_type === 'static').length || 0;
      const dynamicEntries = entriesData?.filter(e => e.entry_type === 'dynamic').length || 0;
      const generatedEntries = entriesData?.filter(e => e.entry_type === 'generated').length || 0;
      const lastGeneration = logsData?.[0]?.created_at;

      return {
        totalEntries,
        activeEntries,
        staticEntries,
        dynamicEntries,
        generatedEntries,
        lastGeneration,
      } as SitemapStats;
    },
  });

  // Generate dynamic entries mutation
  const generateDynamicEntriesMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('generate_dynamic_sitemap_entries');
      if (error) throw error;
      return data;
    },
    onSuccess: (entriesAdded) => {
      toast.success(`Generated ${entriesAdded} new sitemap entries`);
      queryClient.invalidateQueries({ queryKey: ['sitemap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['sitemap-stats'] });
    },
    onError: (error) => {
      console.error('Error generating dynamic entries:', error);
      toast.error('Failed to generate dynamic entries');
    },
  });

  // Toggle entry active status
  const toggleEntryMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('sitemap_entries')
        .update({ is_active, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitemap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['sitemap-stats'] });
    },
    onError: (error) => {
      console.error('Error toggling entry:', error);
      toast.error('Failed to update entry');
    },
  });

  // Delete entry mutation
  const deleteEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('sitemap_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Entry deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['sitemap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['sitemap-stats'] });
    },
    onError: (error) => {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    },
  });

  // Add static entry mutation - fixed return type
  const addStaticEntryMutation = useMutation({
    mutationFn: async ({ url, priority, change_frequency, entry_type }: {
      url: string;
      priority: number;
      change_frequency: string;
      entry_type: string;
    }) => {
      const { data, error } = await supabase
        .from('sitemap_entries')
        .insert({
          url,
          priority,
          change_frequency,
          entry_type,
          is_active: true,
          metadata: { source: 'manual', created_via: 'admin_interface' },
          created_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sitemap-entries'] });
      queryClient.invalidateQueries({ queryKey: ['sitemap-stats'] });
    },
    onError: (error) => {
      console.error('Error adding static entry:', error);
      toast.error('Failed to add static entry');
    },
  });

  // Enhanced sitemap XML generation with comprehensive error handling and debugging
  const generateSitemapXML = async (): Promise<string> => {
    console.log('🚀 Starting sitemap generation...');
    setIsGenerating(true);
    const startTime = Date.now();
    let logId: string | null = null;
    let currentUser: any = null;

    try {
      // Get current user for debugging
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('❌ Error getting current user:', userError);
        throw new Error('Authentication required for sitemap generation');
      }
      
      currentUser = userData.user;
      console.log('👤 Current user ID:', currentUser?.id);

      // Validate user permissions
      if (!currentUser?.id) {
        throw new Error('User authentication required');
      }

      // Create generation log with enhanced error handling
      console.log('📝 Creating generation log...');
      const { data: logData, error: logError } = await supabase
        .from('sitemap_generation_logs')
        .insert({
          generation_type: 'manual',
          status: 'in_progress',
          generated_by: currentUser.id,
          entries_count: 0,
          file_size_bytes: 0,
          generation_time_ms: 0,
        })
        .select()
        .single();

      if (logError) {
        console.error('❌ Error creating generation log:', logError);
        throw new Error(`Failed to create generation log: ${logError.message}`);
      }

      if (!logData?.id) {
        throw new Error('No log ID returned from database');
      }

      logId = logData.id;
      console.log('✅ Generation log created with ID:', logId);

      // Get active entries with validation
      console.log('📊 Fetching active sitemap entries...');
      const { data: activeEntries, error: entriesError } = await supabase
        .from('sitemap_entries')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (entriesError) {
        console.error('❌ Error fetching entries:', entriesError);
        throw new Error(`Failed to fetch sitemap entries: ${entriesError.message}`);
      }

      const entriesCount = activeEntries?.length || 0;
      console.log(`📈 Found ${entriesCount} active entries`);

      if (entriesCount === 0) {
        console.warn('⚠️ No active sitemap entries found');
      }

      // Generate XML with validation
      console.log('🔧 Generating XML content...');
      const baseUrl = window.location.origin;
      const xmlContent = activeEntries?.map(entry => {
        // Validate entry data
        if (!entry.url) {
          console.warn('⚠️ Entry missing URL:', entry.id);
          return null;
        }

        const lastMod = entry.last_modified || entry.updated_at;
        const changeFreq = entry.change_frequency || 'weekly';
        const priority = entry.priority || 0.5;

        return `  <url>
    <loc>${baseUrl}${entry.url}</loc>
    <lastmod>${lastMod}</lastmod>
    <changefreq>${changeFreq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
      }).filter(Boolean).join('\n') || '';

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${xmlContent}
</urlset>`;

      const generationTime = Date.now() - startTime;
      const fileSize = new Blob([xml]).size;

      console.log(`⏱️ Generation completed in ${generationTime}ms`);
      console.log(`📦 File size: ${fileSize} bytes`);

      // Update log with completion - enhanced error handling
      console.log('💾 Updating generation log with completion status...');
      
      const updateData = {
        status: 'completed' as const,
        entries_count: entriesCount,
        file_size_bytes: fileSize,
        generation_time_ms: generationTime,
        completed_at: new Date().toISOString(),
      };

      console.log('📝 Update data:', updateData);

      const { data: updateResult, error: updateError } = await supabase
        .from('sitemap_generation_logs')
        .update(updateData)
        .eq('id', logId)
        .select();

      if (updateError) {
        console.error('❌ Error updating log completion:', updateError);
        console.error('❌ Update error details:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code
        });
        
        // Try to fetch the log to see if it exists
        const { data: existingLog, error: fetchError } = await supabase
          .from('sitemap_generation_logs')
          .select('*')
          .eq('id', logId)
          .single();
          
        if (fetchError) {
          console.error('❌ Error fetching existing log:', fetchError);
        } else {
          console.log('📋 Existing log:', existingLog);
        }
        
        // Don't throw here as XML generation was successful
        toast.error('Sitemap generated but logging failed');
      } else {
        console.log('✅ Log updated successfully:', updateResult);
        toast.success('Sitemap generated successfully');
      }

      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['sitemap-generation-logs'] });
      queryClient.invalidateQueries({ queryKey: ['sitemap-stats'] });

      console.log('🎉 Sitemap generation completed successfully');
      return xml;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('💥 Error generating sitemap:', error);
      
      // Update log with failure status if we have a log ID
      if (logId) {
        console.log('📝 Updating log with failure status...');
        try {
          const failureData = {
            status: 'failed' as const,
            error_message: errorMessage,
            completed_at: new Date().toISOString(),
            generation_time_ms: Date.now() - startTime,
          };

          const { error: failureUpdateError } = await supabase
            .from('sitemap_generation_logs')
            .update(failureData)
            .eq('id', logId);

          if (failureUpdateError) {
            console.error('❌ Error updating failure status:', failureUpdateError);
          } else {
            console.log('✅ Failure status updated successfully');
          }
        } catch (updateError) {
          console.error('💥 Error updating log failure status:', updateError);
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['sitemap-generation-logs'] });
      toast.error(`Failed to generate sitemap: ${errorMessage}`);
      throw error;
    } finally {
      setIsGenerating(false);
      console.log('🏁 Sitemap generation process finished');
    }
  };

  return {
    entries,
    logs,
    stats,
    entriesLoading,
    logsLoading,
    statsLoading,
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    isGenerating,
    generateDynamicEntries: () => generateDynamicEntriesMutation.mutate(),
    toggleEntry: (id: string, is_active: boolean) => 
      toggleEntryMutation.mutate({ id, is_active }),
    deleteEntry: (id: string) => deleteEntryMutation.mutate(id),
    addStaticEntry: (entry: {
      url: string;
      priority: number;
      change_frequency: string;
      entry_type: string;
    }) => addStaticEntryMutation.mutateAsync(entry),
    generateSitemapXML,
  };
};
